/* TodayMobileV2.jsx — iter-196 · Apple × Linear × Stripe operational
 * mission-control surface for the mobile Today page.
 *
 * Mounts only at <md. Desktop TodayPage is untouched.
 *
 * Reads from the same data props the existing TodayMobile receives
 * (needs / followUps / movement / waitingOn / exchangeWeek / deals /
 * findContact / firstName / alertsCount / onRefresh / refreshing /
 * onOpenReview / onOpenQaSheet / onChanged) so this is a pure
 * presentation layer.
 */
import React, { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { cfBus, useChainStore } from "../../data/chainStore";
import {
  Menu, Bell, Sun, Repeat, AlertCircle, Paperclip, FileText,
  RefreshCw, Calendar, Plus, ChevronRight, User, Clock,
} from "lucide-react";

const FALLBACK_THUMB =
  "https://images.unsplash.com/photo-1572120360610-d971b9d7767c?auto=format&fit=crop&w=240&q=60";

const greetingFor = (h) =>
  h < 5 ? "Working late" : h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";

const fmtTime = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
};

/* Status pill mapping mirrors the reference. */
const statusFromBlocker = (b) => {
  const t = String(b?.title || "").toLowerCase();
  const sev = String(b?.severity || "").toLowerCase();
  if (/contract|exchange/.test(t) && sev !== "blocking")
    // An open contract/exchange action is Watch, not "ready" (ready = every required item ticked).
    return { label: "Needs signature",    bg: "bg-amber-50",   text: "text-amber-700",   rail: "#F59E0B", scoreTone: "fair" };
  if (/management|leasehold|doc|memo|lpe1/.test(t))
    return { label: "Awaiting Documents", bg: "bg-violet-50",  text: "text-violet-700",  rail: "#8B5CF6", scoreTone: "fair" };
  if (sev === "blocking" || sev === "warning")
    return { label: "Needs Attention",    bg: "bg-rose-50",    text: "text-rose-600",    rail: "#F97316", scoreTone: "at-risk" };
  return { label: "In progress",        bg: "bg-slate-100",  text: "text-slate-700",   rail: "#94A3B8", scoreTone: "fair" };
};

/* Compact Apple-Fitness-style ring with a gradient stroke. */
function ConfidenceRing({ score, tone }) {
  const palette = tone === "good"
    ? { id: "tmv-good", c1: "#7C3AED", c2: "#6366F1", txt: "text-violet-600", label: "Good"    }
    : tone === "at-risk"
      ? { id: "tmv-risk", c1: "#FB7185", c2: "#F97316", txt: "text-rose-500",   label: "At Risk" }
      : { id: "tmv-fair", c1: "#8B5CF6", c2: "#A78BFA", txt: "text-violet-500", label: "Fair"    };
  const r = 24;
  const c = 2 * Math.PI * r;
  const dash = (Math.max(0, Math.min(100, score)) / 100) * c;
  return (
    <div className="flex flex-col items-center shrink-0">
      <div className="relative w-[62px] h-[62px]">
        <svg viewBox="0 0 60 60" className="w-full h-full -rotate-90">
          <defs>
            <linearGradient id={palette.id} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor={palette.c1} />
              <stop offset="100%" stopColor={palette.c2} />
            </linearGradient>
          </defs>
          <circle cx="30" cy="30" r={r} stroke="#F1F5F9" strokeWidth="3.5" fill="none" />
          <circle
            cx="30" cy="30" r={r}
            stroke={`url(#${palette.id})`}
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeDasharray={`${dash} ${c}`}
            fill="none"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-[17px] font-bold text-slate-900 tabular-nums tracking-[-0.01em]">{score}</span>
        </div>
      </div>
      <p className={`mt-1 text-[10.5px] font-semibold ${palette.txt}`}>{palette.label}</p>
    </div>
  );
}

/* Stable score derived from the blocker so renders are deterministic. */
function deriveScore(item) {
  const sev = String(item?.blocker?.severity || "").toLowerCase();
  const age = Number(item?.blocker?.age_days || 0);
  let s = 78;
  if (sev === "blocking") s = 62;
  else if (sev === "warning") s = 74;
  else if (sev === "monitor") s = 83;
  s -= Math.max(0, age - 2);
  return Math.max(20, Math.min(95, Math.round(s)));
}

/* ─── KPI card ───────────────────────────────────────────────────── */
function KpiCard({ Icon, label, value, sub, tone, testid }) {
  const palette = {
    violet:  { icon: "#7C3AED", iconBg: "rgba(124,58,237,0.10)",  num: "text-violet-700", subTxt: "text-violet-700" },
    rose:    { icon: "#F43F5E", iconBg: "rgba(244,63,94,0.10)",   num: "text-rose-600",   subTxt: "text-rose-600"   },
    amber:   { icon: "#F59E0B", iconBg: "rgba(245,158,11,0.10)",  num: "text-amber-600",  subTxt: "text-amber-600"  },
    indigo:  { icon: "#3B82F6", iconBg: "rgba(59,130,246,0.10)",  num: "text-blue-600",   subTxt: "text-blue-600"   },
  }[tone] || { icon: "#475569", iconBg: "rgba(71,85,105,0.10)", num: "text-slate-700", subTxt: "text-slate-500" };
  return (
    <div
      className="rounded-[22px] bg-white px-3.5 py-3"
      style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 10px 26px -20px rgba(15,23,42,0.14)" }}
      data-testid={testid}
    >
      <span
        className="w-7 h-7 rounded-lg inline-flex items-center justify-center"
        style={{ background: palette.iconBg, color: palette.icon }}
      >
        <Icon size={14} strokeWidth={2.4} />
      </span>
      <p className="text-[11px] font-semibold text-slate-600 mt-2 leading-tight">{label}</p>
      <p className={`text-[24px] font-bold ${palette.num} mt-0.5 leading-none tabular-nums tracking-[-0.01em]`}>{value}</p>
      <p className={`text-[10.5px] font-medium ${palette.subTxt} mt-1`}>{sub}</p>
    </div>
  );
}

/* ─── Operational blocker card ──────────────────────────────────── */
function BlockerCard({ item, onChanged }) {
  const status = statusFromBlocker(item.blocker);
  const score = deriveScore(item);
  // iter-alignment · P0 · Command Centre Data Reconciliation
  // (2026-02-28) — Match the desktop's "Just raised" calm branch
  // when `age_days === 0` (fresh detection / reseed).  Previously
  // this cell floored the age at 1 → phantom "Waiting 1d" even
  // when the blocker was raised today, diverging from every other
  // surface (Priority Row, Waiting-on, Deal Room).
  const ageDays = Number(item.blocker?.age_days || 0);
  const overdue = ageDays >= 1;
  const overdueLabel = overdue ? `Waiting ${ageDays}d` : "Just raised";

  return (
    <Link
      to={`/deal-rooms/${item.deal_id}`}
      className="relative block bg-white rounded-2xl mb-3 overflow-hidden"
      style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 10px 26px -22px rgba(15,23,42,0.14)" }}
      data-testid={`tmv2-blocker-card-${item.deal_id}`}
    >
      <span aria-hidden className="absolute left-0 top-0 bottom-0 w-1" style={{ background: status.rail }} />
      <div className="pl-4 pr-3 py-3 flex items-start gap-3">
        <img
          src={item.thumb_url || FALLBACK_THUMB}
          alt=""
          className="w-[78px] h-[78px] rounded-2xl object-cover bg-slate-100 shrink-0"
          loading="lazy"
        />
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-bold text-slate-900 leading-tight tracking-[-0.01em] line-clamp-2">
            {item.property_address || "Untitled property"}
          </p>
          <span className={`inline-block mt-1.5 px-2 py-0.5 rounded-md text-[10.5px] font-semibold ${status.bg} ${status.text}`}>
            {status.label}
          </span>
          <div className="mt-2 flex items-center flex-wrap gap-x-3 gap-y-1">
            <span className="inline-flex items-center gap-1 text-[11.5px] text-slate-600 min-w-0">
              {/contract|signature/i.test(item.blocker?.title || "") && <User className="w-3 h-3 shrink-0" />}
              {/search|local|enquir/i.test(item.blocker?.title || "") && <FileText className="w-3 h-3 shrink-0" />}
              {!/contract|signature|search|local|enquir/i.test(item.blocker?.title || "") && <FileText className="w-3 h-3 shrink-0" />}
              <span className="truncate max-w-[160px]">{item.blocker?.title || "Operational task"}</span>
            </span>
            <span className="inline-flex items-center gap-1 text-[11.5px] font-medium">
              <span className={`w-1.5 h-1.5 rounded-full ${overdue ? "bg-rose-500" : "bg-slate-300"}`} />
              <span className={overdue ? "text-rose-600" : "text-slate-500"}>{overdueLabel}</span>
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <ConfidenceRing score={score} tone={status.scoreTone} />
          <ChevronRight className="w-3.5 h-3.5 text-slate-300 -ml-0.5" />
        </div>
      </div>
    </Link>
  );
}

/* ─── Today's agenda timeline ───────────────────────────────────── */
function AgendaRow({ ev, isLast }) {
  const time = fmtTime(ev.at) || "—";
  const tone =
    ev.tone === "rose"   ? "#F43F5E" :
    ev.tone === "amber"  ? "#F59E0B" :
    ev.tone === "emerald"? "#10B981" :
                           "#6366F1";
  return (
    <li className="relative flex items-start gap-3 py-3" data-testid={`tmv2-agenda-${ev.id}`}>
      <div className="w-12 text-right shrink-0">
        <p className="text-[14px] font-bold tabular-nums tracking-[-0.005em]" style={{ color: tone }}>
          {time}
        </p>
      </div>
      <div className="relative flex flex-col items-center shrink-0 pt-1.5">
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: tone }} />
        {!isLast && <span className="absolute top-5 bottom-[-12px] w-px bg-slate-200" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[13.5px] font-semibold text-slate-900 leading-tight tracking-[-0.005em] truncate">
          {ev.title}
        </p>
        {ev.subtitle && (
          <p className="text-[11.5px] text-slate-500 mt-0.5 truncate">{ev.subtitle}</p>
        )}
      </div>
      {ev.participants ? (
        <span className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100/80 text-[11px] font-semibold text-slate-600">
          <User className="w-3 h-3" />
          {ev.participants}
        </span>
      ) : null}
    </li>
  );
}

/* ═══════════════════════════════════════════════════════════════════
 * TodayMobileV2 — the page
 * ═══════════════════════════════════════════════════════════════════ */
export function TodayMobileV2({
  needs = [],
  needsAttentionCount = null,
  needsAttentionDeals = null,
  followUps = [],
  movement = [],
  exchangeWeek = [],
  waitingOn = [],
  firstName = "there",
  alertsCount = 0,
  onRefresh,
  refreshing,
  onOpenReview,
  onOpenQaSheet,
  onChanged,
}) {
  const nav = useNavigate();
  // Header bell = unread notifications (same source as the app-wide top bar).
  const { notifications } = useChainStore();
  const unreadNotifications = (notifications || []).filter((n) => !n.read).length;
  const greeting = greetingFor(new Date().getHours());

  /* Derive KPI values from real data. */
  const exchangesThisWeek = exchangeWeek.length || 0;
  const requiresAttention = needsAttentionCount ?? (needs.length || 0);
  const fragileChains    = Math.min(needs.filter((n) => /solicitor|chain/i.test(n.blocker?.title || "")).length || 0, 9);
  const docsUploaded     = movement.filter((m) => /document|upload|pack|contract|search|memo/i.test(m.verb || "")).length || 0;

  const blockerCount = requiresAttention || 0;
  const showBanner = blockerCount > 0;

  /* Derive a calm "Today's agenda" feed from the available data. */
  const agenda = useMemo(() => {
    const items = [];
    for (const e of exchangeWeek.slice(0, 2)) {
      items.push({
        id: `ex-${e.deal_id}`,
        at: e.target_exchange || e.target_iso || null,
        title: `Chain call — ${e.property_address || e.deal_name || "Deal"}`,
        subtitle: "Buyer solicitor, You, Manager",
        tone: "indigo",
        participants: 3,
      });
    }
    for (const f of (followUps || []).slice(0, 3)) {
      items.push({
        id: `fu-${f.deal_id}-${f.id || f.title || ""}`,
        at: f.due_at || f.due_iso || f.due || null,
        title: f.title || "Follow-up due",
        subtitle: f.deal_name || f.property_address,
        tone: f.severity === "blocking" ? "rose" : f.severity === "warning" ? "amber" : "emerald",
        participants: 2,
      });
    }
    // Sort by time when present, else stable.
    return items
      .filter((i) => i.at)
      .sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime())
      .slice(0, 4);
  }, [exchangeWeek, followUps]);

  return (
    <div
      className="md:hidden bg-slate-50/70 min-h-screen"
      style={{ paddingBottom: "calc(7rem + env(safe-area-inset-bottom, 0px))" }}
      data-testid="today-mobile-v2-shell"
    >
      {/* ═══ DARK CINEMATIC HERO ═════════════════════════════════════ */}
      <section
        className="relative overflow-hidden"
        style={{
          background:
            "linear-gradient(180deg, #0B0B14 0%, #1A1640 50%, #2E1F66 100%)",
        }}
        data-testid="tmv2-hero"
      >
        {/* Soft radial purple glow */}
        <span
          aria-hidden
          className="absolute -top-20 right-1/3 w-96 h-96 rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(139,92,246,0.32) 0%, rgba(139,92,246,0) 70%)" }}
        />
        <span
          aria-hidden
          className="absolute top-10 -left-16 w-72 h-72 rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(99,102,241,0.16) 0%, rgba(99,102,241,0) 70%)" }}
        />

        <div className="relative px-5 pt-5 pb-7">
          {/* Top bar */}
          <div className="flex items-center justify-between">
            <button
              type="button"
              aria-label="Menu"
              className="w-11 h-11 rounded-2xl inline-flex items-center justify-center text-white/90"
              style={{
                background: "rgba(255,255,255,0.06)",
                boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08)",
              }}
              data-testid="tmv2-menu"
              onClick={() => window.dispatchEvent(new CustomEvent("cf:sidebar:toggle"))}
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="inline-flex items-center gap-2.5">
              <span
                className="w-7 h-7 rounded-md inline-flex items-center justify-center"
                style={{
                  background: "linear-gradient(135deg, #7C3AED 0%, #6366F1 100%)",
                  boxShadow: "0 0 18px rgba(139,92,246,0.55)",
                }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M3.5 7 a3 3 0 0 1 5.5 0 M5 7 a3 3 0 0 1 5.5 0" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" fill="none" />
                </svg>
              </span>
              <div className="flex flex-col items-start leading-none">
                <span className="text-white text-[10.5px] font-bold tracking-[0.22em]">CHAINFLOW</span>
                <span className="text-white text-[16px] font-bold tracking-[-0.005em] mt-0.5">Flow Centre</span>
              </div>
            </div>
            <button
              type="button"
              aria-label="Notifications"
              className="relative w-11 h-11 rounded-2xl inline-flex items-center justify-center text-white/90"
              style={{
                background: "rgba(255,255,255,0.06)",
                boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08)",
              }}
              data-testid="tmv2-bell"
              onClick={() => (unreadNotifications > 0 ? cfBus.emit("cf:open-notifications") : nav("/notifications"))}
            >
              <Bell className="w-5 h-5" />
              {unreadNotifications > 0 && (
                <span
                  className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full text-[10px] font-bold text-white inline-flex items-center justify-center tabular-nums"
                  style={{
                    background: "linear-gradient(135deg, #7C3AED 0%, #6366F1 100%)",
                    boxShadow: "0 0 14px rgba(139,92,246,0.6)",
                  }}
                >
                  {unreadNotifications > 99 ? "99+" : unreadNotifications}
                </span>
              )}
            </button>
          </div>

          {/* Greeting */}
          <div className="mt-5 flex items-start gap-3">
            <span
              className="w-12 h-12 rounded-2xl inline-flex items-center justify-center shrink-0"
              style={{
                background: "linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)",
                boxShadow: "0 0 22px rgba(139,92,246,0.45)",
              }}
              aria-hidden
            >
              <Sun className="w-5 h-5 text-white" strokeWidth={2.4} />
            </span>
            <div className="flex-1 min-w-0">
              <p
                className="text-white text-[20px] font-bold leading-tight tracking-[-0.01em]"
                data-testid="tmv2-greeting"
              >
                {greeting}, {firstName}
              </p>
              <p className="text-white/65 text-[12.5px] mt-1 leading-snug">
                {requiresAttention} item{requiresAttention === 1 ? "" : "s"} need your attention.
              </p>
            </div>
          </div>

          {/* Updated pill */}
          <div className="mt-4 flex justify-end">
            <span
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-medium text-white/85"
              style={{
                background: "rgba(255,255,255,0.06)",
                boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
              }}
              data-testid="tmv2-live-pill"
            >
              <span className="relative inline-flex items-center justify-center">
                <span className="block w-1.5 h-1.5 rounded-full bg-emerald-400" />
                <span
                  aria-hidden
                  className="absolute inset-0 rounded-full animate-ping"
                  style={{ background: "rgba(52,211,153,0.55)", animationDuration: "2400ms" }}
                />
              </span>
              Updated {refreshing ? "now" : "just now"}
            </span>
          </div>
        </div>

        {/* ═══ KPI ROW — floats over the hero / sheet bridge ════════ */}
        <div className="relative px-4 -mt-1">
          <div className="grid grid-cols-4 gap-2.5" data-testid="tmv2-kpi-row">
            <KpiCard
              Icon={Repeat} label="Exchanges" value={exchangesThisWeek}
              sub="this week" tone="violet" testid="tmv2-kpi-exchanges"
            />
            <KpiCard
              Icon={AlertCircle} label="Needs" value={needsAttentionDeals ?? requiresAttention}
              sub="Attention" tone="rose" testid="tmv2-kpi-requires"
            />
            <KpiCard
              Icon={Paperclip} label="Fragile chains" value={fragileChains}
              sub="monitoring" tone="amber" testid="tmv2-kpi-fragile"
            />
            <KpiCard
              Icon={FileText} label="Docs uploaded" value={docsUploaded}
              sub="today" tone="indigo" testid="tmv2-kpi-docs"
            />
          </div>
        </div>
      </section>

      {/* ═══ WHITE OPERATIONAL SHEET ══════════════════════════════════ */}
      <div className="pt-5 px-4">
        {/* Blocker priority banner */}
        {showBanner && (
          <div
            className="rounded-2xl mb-4 pl-3.5 pr-2 py-2.5 flex items-center gap-3"
            style={{ background: "rgba(254,228,231,0.65)" }}
            data-testid="tmv2-blocker-banner"
          >
            <span
              className="w-10 h-10 rounded-xl inline-flex items-center justify-center shrink-0"
              style={{ background: "rgba(255,255,255,0.8)", color: "#E11D48", boxShadow: "inset 0 0 0 1px rgba(244,63,94,0.16)" }}
              aria-hidden
            >
              <AlertCircle size={18} strokeWidth={2.4} />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[13.5px] font-bold text-rose-700 leading-tight tracking-[-0.005em]">
                {blockerCount} blocker{blockerCount === 1 ? "" : "s"} delaying progression
              </p>
              <p className="text-[11px] text-rose-500/85 mt-0.5 leading-snug">
                You can clear these in under 15 minutes.
              </p>
            </div>
            <button
              type="button"
              onClick={onOpenReview}
              className="h-10 px-4 rounded-2xl inline-flex items-center gap-1.5 text-[12.5px] font-bold text-white shrink-0 transition-transform active:scale-95"
              style={{
                background: "linear-gradient(135deg, #EF4444 0%, #F43F5E 100%)",
                boxShadow: "0 6px 18px -6px rgba(239,68,68,0.55), 0 0 0 1px rgba(244,63,94,0.18)",
              }}
              data-testid="tmv2-review-now"
            >
              Review now <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Quick action strip */}
        <div className="flex items-center gap-2 mb-5" data-testid="tmv2-quick-actions">
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            className="flex-1 h-12 rounded-2xl bg-white inline-flex items-center justify-center gap-2 text-[12.5px] font-semibold text-slate-700"
            style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 8px 20px -16px rgba(15,23,42,0.12)" }}
            data-testid="tmv2-qa-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => onOpenReview && onOpenReview()}
            className="relative flex-1 h-12 rounded-2xl bg-white inline-flex items-center justify-center gap-2 text-[12.5px] font-semibold text-slate-700"
            style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 8px 20px -16px rgba(15,23,42,0.12)" }}
            data-testid="tmv2-qa-alerts"
          >
            <Bell className="w-3.5 h-3.5" />
            Alerts
            {alertsCount > 0 && (
              <span
                className="ml-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white inline-flex items-center justify-center tabular-nums"
                style={{ background: "#E11D48" }}
              >
                {Math.min(alertsCount, 99)}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => nav("/calendar")}
            className="flex-1 h-12 rounded-2xl bg-white inline-flex items-center justify-center gap-2 text-[12.5px] font-semibold text-slate-700"
            style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 8px 20px -16px rgba(15,23,42,0.12)" }}
            data-testid="tmv2-qa-calendar"
          >
            <Calendar className="w-3.5 h-3.5" />
            Calendar
          </button>
          <button
            type="button"
            onClick={() => nav("/deal-rooms?create=1")}
            className="flex-[1.3] h-12 rounded-2xl inline-flex items-center justify-center gap-2 text-[12.5px] font-bold text-white transition-transform active:scale-95"
            style={{
              background: "linear-gradient(135deg, #7C3AED 0%, #6366F1 100%)",
              boxShadow: "0 8px 22px -6px rgba(124,58,237,0.55), 0 0 0 1px rgba(124,58,237,0.18)",
            }}
            data-testid="tmv2-qa-new-deal"
          >
            <Plus className="w-3.5 h-3.5" />
            New deal
          </button>
        </div>

        {/* Operational blockers */}
        <section className="mb-6" data-testid="tmv2-blockers-section">
          <header className="flex items-center justify-between mb-3 px-1">
            <div className="inline-flex items-center gap-2">
              <h2 className="text-[16.5px] font-bold text-slate-900 tracking-[-0.01em]">
                Operational blockers
              </h2>
              {blockerCount > 0 && (
                <span
                  className="text-[10.5px] font-bold tabular-nums px-2 py-0.5 rounded-full"
                  style={{ background: "rgba(244,63,94,0.10)", color: "#E11D48" }}
                >
                  {blockerCount}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={onOpenReview}
              className="text-[12px] font-semibold text-violet-700 inline-flex items-center gap-0.5"
              data-testid="tmv2-blockers-view-all"
            >
              View all <ChevronRight className="w-3 h-3" />
            </button>
          </header>

          {needs.length === 0 ? (
            <div className="rounded-2xl bg-white p-6 text-center" style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
              <p className="text-[12.5px] text-slate-500">All blockers cleared — calm pipeline.</p>
            </div>
          ) : (
            needs.slice(0, 6).map((item, i) => (
              <BlockerCard key={`${item.deal_id}-${item.blocker?.id || i}`} item={item} onChanged={onChanged} />
            ))
          )}
        </section>

        {/* Today's agenda */}
        {agenda.length > 0 && (
          <section className="mb-6" data-testid="tmv2-agenda-section">
            <header className="flex items-center justify-between mb-2 px-1">
              <div className="inline-flex items-center gap-2">
                <h2 className="text-[16.5px] font-bold text-slate-900 tracking-[-0.01em]">
                  Today's agenda
                </h2>
                <span
                  className="text-[10.5px] font-bold tabular-nums px-2 py-0.5 rounded-full bg-violet-50 text-violet-700"
                >
                  {agenda.length}
                </span>
              </div>
              <Link
                to="/calendar"
                className="text-[12px] font-semibold text-violet-700 inline-flex items-center gap-0.5"
                data-testid="tmv2-agenda-view-calendar"
              >
                View calendar <ChevronRight className="w-3 h-3" />
              </Link>
            </header>
            <div
              className="rounded-2xl bg-white px-3 py-1"
              style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 10px 26px -22px rgba(15,23,42,0.14)" }}
            >
              <ul className="divide-y divide-slate-100">
                {agenda.map((ev, i) => (
                  <AgendaRow key={ev.id} ev={ev} isLast={i === agenda.length - 1} />
                ))}
              </ul>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

export default TodayMobileV2;
